/**
 * THE NUMBERS THE LANDING PAGE IS ALLOWED TO STATE.
 *
 * ── WHY THIS FILE EXISTS, AND WHY IT DOES NOT IMPORT THE SIM ────────────────
 * The landing page is the first thing a visitor reads, and until now it told
 * them the game had THREE lanes and lasted TWO MINUTES. Both were wrong: the
 * board has two lanes split by a river, and a match is 3:00 of regulation plus
 * up to 2:00 of sudden death. Nobody wrote those sentences dishonestly — they
 * were typed once, early, into a translation file, and no test has ever been
 * able to disagree with a string.
 *
 * So the numbers live here, as data, and `test/landing-facts.test.ts` asserts
 * every one of them against its real source — the sim package for the rules
 * and the card catalogue, `crate.data.ts` for the crate ladder. Change the
 * engine and
 * the test fails until the page is corrected. That is the whole point: the copy
 * cannot drift away from the game in silence.
 *
 * It deliberately does NOT import the sim package, because `main.ts` reaches
 * the landing statically and that barrel would land in the shell budget
 * (ADR-0005 — `bootstrap.ts` carries the same note about `TICK_HZ`). The test
 * pays that import cost instead, where no visitor does.
 *
 * EVERY constant below is interpolated into a translated sentence rather than
 * spelled out in one, so a translator cannot get a number wrong and no locale
 * can fall behind the engine.
 */

/** Lanes on the board — `LANE_COUNT`. Left and right, split by the river. */
export const LANES = 2;

/** Towers a side defends — `TOWER_SLOT_COUNT`: the Keep and two Bastions. */
export const TOWERS_PER_SIDE = 3;

/** Sigils in a match — `SIGIL_MAX`. Taking the Keep is worth all three. */
export const SIGILS = 3;

/** Cards in a deck — `DECK_SIZE`. */
export const DECK_SIZE = 8;

/** Cards in hand at once — `HAND_SIZE`; the other four queue behind them. */
export const HAND_SIZE = 4;

/** Regulation, in minutes — `REGULATION_TICKS` / `TICKS_PER_SECOND` / 60. */
export const REGULATION_MINUTES = 3;

/** Sudden death, in minutes — `OVERTIME_TICKS` / `TICKS_PER_SECOND` / 60. */
export const OVERTIME_MINUTES = 2;

/** Mana ceiling — `MANA_MAX`. */
export const MANA_MAX = 10;

/**
 * Seconds per mana at the base rate — `MANA_SUBTICKS_PER_UNIT` (56) divided by
 * `TICKS_PER_SECOND` (20). Exact, not rounded: the engine counts subticks so
 * the ladder never drifts.
 */
export const MANA_SECONDS = 2.8;

/** Minutes remaining in regulation when the mana rate doubles. */
export const DOUBLE_MANA_AT_MINUTES = 2;

/** Seconds of overtime remaining when the rate triples. */
export const TRIPLE_MANA_AT_SECONDS = 60;

/** Server simulation rate, in hertz — `TICKS_PER_SECOND`. */
export const TICK_HZ = 20;

/** Cards in the catalogue — `CARD_IDS.length`. */
export const CARD_TOTAL = 52;

/** Of those, how many are units, spells and structures. */
export const CARD_UNITS = 40;
export const CARD_SPELLS = 8;
export const CARD_STRUCTURES = 4;

/** Playable factions, not counting Neutral, which anyone may run. */
export const FACTION_COUNT = 4;

/** Rarities: common, rare, epic, mythic. */
export const RARITY_COUNT = 4;

/** Deploy cost range across the catalogue, in mana. */
export const COST_MIN = 1;
export const COST_MAX = 8;

/** Card level ceiling — `MAX_LEVEL`. */
export const MAX_CARD_LEVEL = 14;

/**
 * Ladder tiers — the arenas, then the leagues above them.
 *
 * TEN, not nine. The first version of this file said nine, read off
 * `ladder.rules.ts`'s "Nine arenas (0..8) plus five leagues = fourteen tiers"
 * — which is CORRECT about the thing it describes and was the wrong thing to
 * read. That comment is about core's season REWARD TABLE, where arena 9 is
 * deliberately omitted because its threshold is the league floor and two rows
 * sharing a `minPoints` would make the lookup pick by sort order.
 *
 * A player climbing the ladder passes through all ten arenas and then five
 * leagues: fifteen named tiers. Bound to `ARENAS.length` by
 * `landing-facts.test.ts` now, which is what should have caught this instead
 * of a second reading of a comment.
 */
export const ARENA_COUNT = 10;
export const LEAGUE_COUNT = 5;

/** Tiers on a season's Warpath — `WARPATH_TIERS`. */
export const WARPATH_TIERS = 35;

/** Crate tiers — `CRATE_DEFINITIONS.length`. */
export const CRATE_TIERS = 4;

/** Crates a player can hold unopened at once — `CRATE_SLOT_COUNT`. */
export const CRATE_SLOTS = 4;

/** Hours the slowest crate takes to unlock — `riftgate-hoard`. */
export const CRATE_MAX_HOURS = 24;

/* ═══════════════════════════════ THE PAGE THAT SHOWS THE GAME, NOT TELLS IT ══
 *
 * Everything below feeds the four picture sections the owner asked for: "views
 * of the towers, elements, board and other things to play". Each of them names
 * a real file that already ships in `frontend/public/` and a real figure that
 * `landing-facts.test.ts` binds back to `@rga/sim`.
 *
 * It is DATA rather than markup for the same reason the constants above are:
 * the moment a sprite id, a mana cost or a hit-point total is typed into a
 * sentence in eight locale files, nothing can disagree with it again. The
 * failure this whole file exists to prevent ("three lanes", "two minutes") was
 * exactly that, and a roster is a much bigger surface to get wrong — forty
 * units, four factions, and an art folder another track keeps regenerating.
 *
 * NOTHING HERE MAY BECOME A `t()` KEY LOOKUP BY ACCIDENT. The `key` fields are
 * bare tails (`unit_cinderhound`), composed by the page as
 * `t(`landing.${key}.title`)`. That is a contract with
 * `test/i18n-completeness.test.ts`, whose scanner treats a quoted literal whose
 * head is a shipped namespace as a `t()` argument and rejects one that names a
 * subtree — see the long note above `CHAMPIONS` in `guide-page.ts`.
 */

/* ───────────────────────────────────────────────────────────────── the board */

/** Board width in tiles — `BOARD_W`. */
export const BOARD_TILES_W = 18;
/** Board depth in tiles — `BOARD_H`. Portrait: you look up the lanes. */
export const BOARD_TILES_H = 32;
/** Rows of impassable water down the middle — `RIVER_Y1 - RIVER_Y0 + 1`. */
export const RIVER_ROWS = 2;
/** Tiles wide a bridge is — `BRIDGE_X1[l] - BRIDGE_X0[l] + 1`. One per lane. */
export const BRIDGE_TILES = 3;
/** Tower pads on the board — `TOWER_PADS.length`: three a side. */
export const TOWER_PADS_TOTAL = 6;

/* ──────────────────────────────────────────────────────────────── the towers */

/**
 * The level the §2 tower figures are anchored at — `TOWER_REFERENCE_LEVEL`.
 *
 * Quoted with the hit points every time, because a tower's HP is a LEVEL TABLE
 * (`buildAnchoredLevelTable`) and "4,008 hit points" on its own is a number
 * that is true at exactly one of fourteen levels.
 */
export const TOWER_LEVEL = 11;
/** Keep hit points at {@link TOWER_LEVEL}. Bound to `towerHp()` by `landing-facts.test.ts`. */
export const KEEP_HP = 4008;
/** Bastion hit points at {@link TOWER_LEVEL}, both bodies. Bound to `towerHp()` by that same test. */
export const BASTION_HP = 2534;

/**
 * The three tower sprites, in board order: the Keep, then the two Bastions.
 *
 * `img` is the BASE prop frame the match renderer falls through to when nobody
 * is wearing a tower skin (`match/render/board.ts` — `tower-keep`,
 * `tower-bastion-a`, `tower-bastion-b`). It is the art a player actually sees
 * on a default board, which is the honest thing to put on a landing page; the
 * fifteen skinned variants get their own tile in the cosmetics section.
 *
 * `tower-bastion-a` is the LEFT lane and `-b` the RIGHT — `propIdForSlot()`
 * decides that, and `tower-skin.test.ts` pins it. The two are one tower in the
 * rules (identical `BASTION_STATS_BASE`) and two different builds in the art,
 * and the copy says so rather than implying a third set of stats.
 */
export const TOWERS = [
  { key: 'tower_keep', img: '/sprites/props/frames/tower-keep/intact-near.png', w: 338, h: 336 },
  {
    key: 'tower_bastion_left',
    img: '/sprites/props/frames/tower-bastion-a/intact-near.png',
    w: 183,
    h: 256,
  },
  {
    key: 'tower_bastion_right',
    img: '/sprites/props/frames/tower-bastion-b/intact-near.png',
    w: 160,
    h: 256,
  },
] as const;

/* ─────────────────────────────────────────────────────────── the board plate */

/**
 * The board plate the page shows, and why it is a SHOP TILE rather than the
 * arena itself.
 *
 * `dist/assets/arenas/riftgate-span/background.png` is the real board: 1536 x
 * 2688, and 9.5 MB. Putting that in front of a visitor on 4G would cost more
 * than the rest of the site put together, and `CLAUDE.md` budgets the whole
 * first load at three seconds. The arena-skin shop tile is the same arena,
 * cropped to the middle band where the rift and both bridges are, at 512 x 384
 * and 412 KB — the one view of the board that exists in this repo at a size a
 * landing page can pay for.
 *
 * It is also honest about being a crop: `landing.board_alt` describes the rift,
 * the bridges and the corner pads, which is what is in the frame. It does NOT
 * claim to be the whole board; the figures beside it say how big that is.
 *
 * If a portrait plate of the full board is ever baked at landing size, this is
 * the one line to change.
 */
export const BOARD_PLATE = '/cosmetics/cosmetic-arena-skin-riftgate-span.png';
export const BOARD_PLATE_W = 512;
export const BOARD_PLATE_H = 384;

/**
 * Where the three legend markers sit on that plate, in per-cent of its box.
 *
 * Deliberately placed on LARGE, unambiguous regions — the rift spans the full
 * width at mid-height, a bridge is a fifth of the width, a pad owns its whole
 * corner — because these were read off the image rather than computed from
 * `board.ts`, and a marker that is three per cent out must still be pointing at
 * the right thing. The markers are `aria-hidden`; the ordered list under the
 * plate is what actually carries the meaning, and it reads correctly with no
 * image at all.
 */
export const BOARD_MARKS = [
  { key: 'board_river', x: 48, y: 47 },
  // 29, not the 21 this shipped with: at 21 per cent the bridge marker sat on
  // the RIVER just left of the bridge deck, which is marker 1's job. Nudged
  // after looking at the rendered plate at 320 px — the left bridge runs from
  // about 24 to 37 per cent, so this is its middle.
  { key: 'board_bridge', x: 29, y: 45 },
  { key: 'board_pad', x: 10, y: 10 },
] as const;

/* ────────────────────────────────────────────────────────────────── the units */

/**
 * EIGHT UNITS, TWO PER BANNER — the spread a visitor is actually deciding about.
 *
 * Chosen for three things at once, in this order:
 *   1. ROLE. Each pair is a cheap commitment and an expensive one, and across
 *      the eight there is a charger, a swarm, a flier, a tank, a healer-adjacent
 *      spawner, a chainer and a siege piece. A roster of eight big mythics
 *      would show the art and hide the game.
 *   2. TRUTH. Every claim the copy makes is asserted against the card JSON in
 *      `landing-facts.test.ts` — the costs and hit points below, and the
 *      ABILITIES the sentences describe. "Double damage on the charge" is
 *      `dmgPct: 200` through `pct()`; "two every six seconds" is
 *      `period: 120` at 20 Hz. A balance pass that moves either fails the test.
 *   3. BYTES. The cut-outs run 39–143 KB each and the eight below total ~540 KB.
 *      That is the whole reason this list is eight and not forty: the section
 *      is lazy and below the fold, but a visitor who scrolls still pays for it.
 *
 * `img` is the RELIT CUT-OUT (`/sprites/heroes/<cardId>.png`), not the 560 px
 * `/heroes/` render the champions strip used to draw. Same characters, a fifth
 * of the bytes, and there are sixty-seven of them instead of three.
 */
export const ROSTER = [
  {
    key: 'unit_cinderhound',
    id: 'ember-cinderhound',
    img: '/sprites/heroes/ember-cinderhound.png',
    color: '#e0642e',
    cost: 2,
    count: 2,
    hp: 320,
    w: 114,
    h: 256,
  },
  {
    key: 'unit_forgetitan',
    id: 'ember-forgetitan',
    img: '/sprites/heroes/ember-forgetitan.png',
    color: '#e0642e',
    cost: 6,
    count: 1,
    hp: 1700,
    w: 299,
    h: 256,
  },
  {
    /*
     * GRAVEBELL, and NOT Pallwisp, which this entry was first.
     *
     * A Pallwisp is `deckable: false` — a spawn-only token that never appears
     * in a deck, a shop or a crate. It read perfectly as a one-mana flier and
     * would have been a card on the front page that no visitor can ever play.
     * `landing-facts.test.ts` asks every entry here for its `deckable` flag now,
     * which is what caught it; the copy for this card names the Pallwisp in the
     * only place it truthfully belongs, as the thing this one breaks into.
     */
    key: 'unit_gravebell',
    id: 'hollow-gravebell',
    img: '/sprites/heroes/hollow-gravebell.png',
    color: '#8b6cff',
    cost: 4,
    count: 1,
    hp: 700,
    w: 104,
    h: 256,
  },
  {
    key: 'unit_charnel_ox',
    id: 'hollow-charnel-ox',
    img: '/sprites/heroes/hollow-charnel-ox.png',
    color: '#8b6cff',
    cost: 5,
    count: 1,
    hp: 2040,
    w: 133,
    h: 256,
  },
  {
    key: 'unit_bramblekin',
    id: 'thorn-bramblekin',
    img: '/sprites/heroes/thorn-bramblekin.png',
    color: '#5fbf6a',
    cost: 3,
    count: 4,
    hp: 210,
    w: 225,
    h: 256,
  },
  {
    key: 'unit_elderbriar',
    id: 'thorn-elderbriar',
    img: '/sprites/heroes/thorn-elderbriar.png',
    color: '#5fbf6a',
    cost: 6,
    count: 1,
    hp: 2750,
    w: 232,
    h: 256,
  },
  {
    key: 'unit_rimeray',
    id: 'tide-rimeray',
    img: '/sprites/heroes/tide-rimeray.png',
    color: '#3aa8d8',
    cost: 3,
    count: 1,
    hp: 540,
    w: 252,
    h: 256,
  },
  {
    key: 'unit_brinecaster',
    id: 'tide-brinecaster',
    img: '/sprites/heroes/tide-brinecaster.png',
    color: '#3aa8d8',
    cost: 4,
    count: 1,
    hp: 660,
    w: 165,
    h: 256,
  },
] as const;

/** Seconds between an Elderbriar's spawns — `period: 120` ticks at `TICK_HZ`. */
export const ELDERBRIAR_SPAWN_SECONDS = 6;

/* ─────────────────────────────────────────────────────────────── cosmetics ── */

/**
 * Cosmetics in the catalogue — one `cosmetics.<kind>.<slug>.name` each.
 *
 * SEVENTY-FOUR, not the seventy-five tiles in `public/cosmetics/`.
 * `cosmetic-emote-salute.png` ships with no catalogue name, so nothing can
 * render it with a label and no player can be shown it by name — counting it
 * would put a number on the front page that the game cannot back up. The
 * orphan tile is a shop/catalogue matter, not this page's.
 */
export const COSMETIC_TOTAL = 74;
/** Kinds they are sorted into — `WARDROBE_KINDS.length`. */
export const COSMETIC_KINDS = 8;

/**
 * ONE TILE PER KIND, and the tile is the shop's own 512 x 384 preview.
 *
 * FIVE of the eight kinds, and the three that are missing were cut by the byte
 * budget rather than by taste: `avatar`, `banner` and `frame` are three more
 * 120–210 KB PNGs that all say the same thing (they dress your profile card),
 * and `test/landing-showcase.test.ts` failed the page at 1,901 KB with the
 * banner tile in. The five that remain are the ones you see DURING a match —
 * the board, the towers, the emote wheel, the deploy flourish — plus the title,
 * which is free at 9 KB. The section's own copy says there are seventy-four.
 *
 * THE SLUG OF EACH TILE IS THE CHEAPEST ONE ITS KIND SHIPS, because which item
 * of a kind is shown says nothing and its weight says a lot. Measured across
 * `public/cosmetics/`: the thirteen `deploy-vfx` tiles run 105–142 KB and
 * `gilded-rift` is the smallest; the eight `title` tiles run 9–13 KB and
 * `rift-initiate` is the smallest. Picking by name instead would have cost
 * 37 KB for a picture nobody can tell apart from this one at rail size.
 *
 * This list stood at THREE for one revision — `deploy-vfx` and `title` were
 * dropped while the byte guard was being fought, and the prose above, which
 * still said five, was what caught it. `landing-showcase.test.ts` pins the
 * length at five for that reason: a derived count would have passed the drop.
 *
 * `arena-skin` is FIRST and costs nothing: it is the same URL the board section
 * above already loaded, so the browser serves it from cache — and it makes the
 * point better than any new image could, because the board the visitor has just
 * been reading about turns out to be an item.
 *
 * Both strings each tile renders already exist in all eight catalogues:
 * `cosmetics.<kind>.<slug>.name` names it and `wardrobe.where.<kind>` says
 * where it shows up in a match. Writing new copy here would have meant eight
 * more translations saying what the wardrobe already says correctly.
 */
export const COSMETIC_SHOWCASE = [
  { kind: 'arena-skin', slug: 'riftgate-span' },
  { kind: 'tower-skin', slug: 'riftgate-gilt' },
  { kind: 'emote', slug: 'nice-try' },
  { kind: 'deploy-vfx', slug: 'gilded-rift' },
  { kind: 'title', slug: 'rift-initiate' },
] as const;

/** Every cosmetic tile is this size. The shop bakes them to one box. */
export const COSMETIC_TILE_W = 512;
export const COSMETIC_TILE_H = 384;

/* ──────────────────────────────────────────────────────────────── the House ── */

/**
 * The finished Seamhold, for the progression section.
 *
 * `SEAMHOLD_STAGES` is sixteen plates, `stage-00-base` … `stage-15-seam-lantern`,
 * and a House builds through them together. The landing page shows the LAST one
 * because the question it answers is "what is this for", and because at 250 KB
 * for 1920 x 1080 it is the best bytes-per-pixel asset in the repository — the
 * bar every other image here is measured against.
 */
export const SEAMHOLD_PLATE = '/houses/seamhold/stage-15-seam-lantern.webp';
export const SEAMHOLD_PLATE_W = 1920;
export const SEAMHOLD_PLATE_H = 1080;
/** Plates in the build sequence — `SEAMHOLD_STAGES.length`. */
export const SEAMHOLD_STAGE_COUNT = 16;
